import React, {useEffect, useState} from 'react'
import {Box, Button, Typography} from '@mui/material'
import SerialsEdit from './SerialsEdit'
import NonSerialsEdit from './NonSerialsEdit'
import {isSpecialBin, parseBinName, parseBinString, SaveInventoryItem, SaveInventoryRequestItem} from '../../utils'

interface BinEditProps {
    serialItems: SaveInventoryItem[]
    nonSerialItems: SaveInventoryItem[]
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
    currentBin: string | null
    onSetCurrentBin: (currentBin: string) => void
    onBackToSummary: () => void
    nextBin: string | null
    prevBin: string | null
    row: string
}

interface BinDetails {
    totalCount: number
    serialCount: number
    nonSerialCount: number
    excludedCount: number
}

type ButtonColor = 'primary' | 'inherit' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | undefined

const BinEdit = ({
                     serialItems,
                     nonSerialItems,
                     saveRequestItem,
                     currentBin,
                     onSetCurrentBin,
                     onBackToSummary,
                     nextBin,
                     prevBin,
                     row
                 }: BinEditProps) => {
    const [selectedSection, setSelectedSection] = useState<string | null>(null)
    const [binDescription, setBinDescription] = useState<string>('')
    const [binDetails, setBinDetails] = useState<BinDetails | null>(null)
    const [nextButtonLabel, setNextButtonLabel] = useState('Next Bin')
    const [prevButtonLabel, setPrevButtonLabel] = useState('Previous Bin')
    const [nextButtonColor, setNextButtonColor] = useState<ButtonColor>('primary')
    const [prevButtonColor, setPrevButtonColor] = useState<ButtonColor>('primary')

    useEffect(() => {
        if (!currentBin) {
            setBinDetails(null)
            setBinDescription('')
            return
        }

        let section = window.sessionStorage.getItem('selectedSection')
        if (section !== 'nonSerials') {
            setSelectedSection('serials')
            window.sessionStorage.setItem('selectedSection', 'serials')
        }

        setBinDetails(calcBinDetails(currentBin))
        setBinDescription(generateBinDescription(currentBin))
    }, [currentBin, serialItems, nonSerialItems])

    const handleSelectSection = (section: string) => {
        setSelectedSection(section)
        window.sessionStorage.setItem('selectedSection', section)
    }

    const handleDeselect = () => {
        setSelectedSection('')
        window.sessionStorage.setItem('selectedSection', '')
    }

    const generateBinDescription = (str: string): string => {
        let {row, sleeve, bin} = parseBinString(str)
        if (isSpecialBin(str)) {
            return `Row: ${row} ${'\u00A0'.repeat(2)}| ${'\u00A0'.repeat(
                2
            )} Sleeve: N/A ${'\u00A0'.repeat(2)} | ${'\u00A0'.repeat(
                2
            )} Bin: N/A`
        }
        return `Row: ${row} ${'\u00A0'.repeat(2)}| ${'\u00A0'.repeat(
            2
        )} Sleeve: ${sleeve} ${'\u00A0'.repeat(2)} | ${'\u00A0'.repeat(
            2
        )} Bin: ${bin}`
    }

    const calcBinDetails = (currentBin: string): BinDetails => {
        let serials = serialItems.filter(item => item.bin === currentBin &&
            (!item.progress || item.progress === 'q-delete' || item.progress === 'deleting'))
        let nonSerials = nonSerialItems.filter(item => item.bin === currentBin &&
            (!item.progress || item.progress === 'q-delete' || item.progress === 'deleting'))
        let serialCount = serials.filter(item => !item.excluded).length
        let nonSerialCount = 0
        let nonExcludedNonSerials = nonSerials.filter(item => !item.excluded)
        nonExcludedNonSerials.forEach(item => {
            nonSerialCount += item.quantity
        })

        let totalCount = nonSerialCount + serialCount
        let excludedCount = serialItems.filter(item => item.excluded).length

        return {
            totalCount,
            serialCount,
            nonSerialCount,
            excludedCount
        }
    }

    useEffect(() => {
        if (currentBin && nextBin && prevBin) {
            let currentSleeve = parseBinString(currentBin).sleeve
            let nextSleeve = parseBinString(nextBin).sleeve
            let prevSleeve = parseBinString(prevBin).sleeve

            let nextBinRow = parseBinString(nextBin).row
            let prevBinRow = parseBinString(prevBin).row

            if (nextBinRow !== row) {
                setNextButtonLabel('Next Row')
                setNextButtonColor('secondary')
            } else if (nextSleeve !== currentSleeve) {
                setNextButtonLabel('Next Sleeve')
                setNextButtonColor('secondary')
            } else {
                setNextButtonLabel('Next Bin')
                setNextButtonColor('primary')
            }

            if (prevBinRow !== row) {
                setPrevButtonLabel('Previous Row')
                setPrevButtonColor('secondary')
            } else if (prevSleeve !== currentSleeve) {
                setPrevButtonLabel('Previous Sleeve')
                setPrevButtonColor('secondary')
            } else {
                setPrevButtonLabel('Previous Bin')
                setPrevButtonColor('primary')
            }
        } else if (currentBin && prevBin) {
            setNextButtonLabel('Next Bin')
            setNextButtonColor('primary')
            setPrevButtonLabel('Previous Row')
            setPrevButtonColor('secondary')
        } else {
            setNextButtonLabel('Next Bin')
            setNextButtonColor('primary')
            setPrevButtonLabel('Previous Bin')
            setPrevButtonColor('primary')
        }
    }, [nextBin, prevBin, currentBin, row])

    if (!currentBin) {
        return (
            <>
                <div className="text-center text-red-600">Bin not found</div>
                <div className="flex mt-6 justify-center">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onBackToSummary}>
                        Back to Summary
                    </Button>
                </div>
            </>
        )
    } else {
        return (
            <Box p={6} height="full" onClick={handleDeselect}>
                <Typography variant="subtitle1" align="center" gutterBottom>
                    {binDescription}
                </Typography>
                <Typography variant="h4" align="center" gutterBottom>
                    {parseBinName(currentBin)}
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    color="textSecondary"
                    gutterBottom>
                    <span style={{fontWeight: 700, color: '#2e7d32'}}>
                        Saved:{' '}
                    </span>
                    {binDetails && (
                        <>
                            {`${binDetails.totalCount} quantity`},{' '}
                            {binDetails.serialCount === 1 ? `${binDetails.serialCount} serial` : `${binDetails.serialCount} serials`},{' '}
                            {binDetails.nonSerialCount === 1 ? `${binDetails.nonSerialCount} non-serial` : `${binDetails.nonSerialCount} non-serials`},{' '}
                            <span style={{color: 'red'}}>{binDetails.excludedCount} excluded </span>
                        </>
                    )}
                </Typography>

                <Box mb={2} mt={4} display="flex" justifyContent="space-between">
                    {!isSpecialBin(currentBin) && (
                        <Button
                            variant="contained"
                            color={prevButtonColor}
                            onClick={() => {
                                currentBin && onSetCurrentBin(prevBin || '')
                                setSelectedSection('serials')
                            }}
                            disabled={!prevBin}>
                            {prevButtonLabel} {parseBinName(prevBin) || ''}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onBackToSummary}>
                        View Summary
                    </Button>
                    {!isSpecialBin(currentBin) && (
                        <Button
                            variant="contained"
                            color={nextButtonColor}
                            onClick={() => {
                                currentBin && onSetCurrentBin(nextBin || '')
                                setSelectedSection('serials')
                            }}
                            disabled={!nextBin}>
                            {nextButtonLabel} {parseBinName(nextBin) || ''}
                        </Button>
                    )}
                </Box>

                <Box display="flex" justifyContent="space-between" gap={2}>
                    <SerialsEdit
                        serialItems={serialItems}
                        selectedSection={selectedSection}
                        onSelectSection={handleSelectSection}
                        saveRequestItem={saveRequestItem}
                        currentBin={currentBin}
                    />
                    <NonSerialsEdit
                        nonSerialItems={nonSerialItems}
                        selectedSection={selectedSection}
                        onSelectSection={handleSelectSection}
                        saveRequestItem={saveRequestItem}
                        currentBin={currentBin}
                    />
                </Box>
            </Box>
        )
    }
}

export default BinEdit
